<template lang="pug">
    div(v-if="validTicket")
        v-card.mx-auto.mb-4(elevation="2" rounded="lg")
            v-card-title.pb-2(style="background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%); color: white;")
                .d-flex.justify-space-between.align-center
                    .d-flex.flex-column.flex-grow-1
                        .text-h6.font-weight-medium {{ ticket.title }}
                        .d-flex.align-center.mt-1
                            v-chip(
                                small
                                color="white"
                                text-color="#1976d2"
                                class="mr-2 ticket-key-chip"
                                style="font-size: 14px; font-weight: 700; letter-spacing: 1px;"
                            ) {{ ticket.ticket_key }}
                            span.account-label.font-weight-bold.text-uppercase(:style="'color: white; font-size: 13px; letter-spacing: 0.5px; margin-left: 2px;'") {{ ticket.organization_id > 0 ? 'Organization' : 'Account' }}
                            template(v-if="validTicket && ticket.jira_issue_id?.length > 0 && currentUserIsSuperAdmin")
                                .ml-2
                                    v-tooltip(top)
                                        template(v-slot:activator='{ on, attrs }')
                                            span(v-bind="attrs" v-on="on" role="text")
                                                v-btn(color="success" small icon :href="jiraIssueLink" target="_blank")
                                                    v-icon mdi-check-circle
                                        span
                                            | This Ticket has been Escalated to Engineering
                            v-btn(
                                v-if="editable"
                                icon
                                small
                                color="white"
                                :to="{ name: 'ticket_show', params: { ticket_key: ticket.ticket_key } }"
                            ).ml-2
                                v-icon mdi-pencil


            v-card-text.pt-2.pb-3
                .mb-2
                    span.text-caption.font-weight-bold.text-uppercase.mb-1(style="color: rgba(0, 0, 0, 0.6);") Account
                    br
                    span.text-h6.font-weight-medium {{ ticket.account_name }}
                .mb-3
                    span.text-caption.font-weight-bold.text-uppercase.mb-1(v-if="!editable" style="color: rgba(0, 0, 0, 0.6); display: inline-block; margin-right: 8px;") Description
                    span.text-body-2(
                        v-if="!editable"
                        v-html="ticket.description"
                        style="color: rgba(0, 0, 0, 0.87); line-height: 1.6;"
                    )

            v-card-text.pt-0
                v-row
                    v-col(cols="12" md="4")
                        .mb-3
                            .text-caption.font-weight-bold.text-uppercase.mb-1(style="color: rgba(0, 0, 0, 0.6);") Created At
                            .text-body-2
                                CopyableText(
                                    v-if="ticket.friendly_created_at"
                                    :text="ticket.friendly_created_at.toString()"
                                )
                                template(v-else) ---
                        
                        .mb-3
                            .text-caption.font-weight-bold.text-uppercase.mb-1(style="color: rgba(0, 0, 0, 0.6);") Updated At
                            .text-body-2
                                CopyableText(
                                    v-if="ticket.friendly_updated_at"
                                    :text="ticket.friendly_updated_at.toString()"
                                )
                                template(v-else) ---

                    v-col(cols="12" md="4")
                        .mb-3
                            .text-caption.font-weight-bold.text-uppercase.mb-1(style="color: rgba(0, 0, 0, 0.6);") Type
                            .text-body-2
                                template(v-if="ticket.ticket_type")
                                    span(
                                        :class="getTypeClass(ticket.ticket_type)"
                                        style="font-weight: 600;"
                                    ) {{ ticket.ticket_type | titleize }}
                                template(v-else) ---
                        
                        .mb-3
                            .text-caption.font-weight-bold.text-uppercase.mb-1(style="color: rgba(0, 0, 0, 0.6);") Status
                            .text-body-2
                                template(v-if="ticket.current_status")
                                    span(
                                        :class="getStatusClass(ticket.current_status)"
                                        style="font-weight: 600;"
                                    ) {{ ticket.current_status | titleize }}
                                template(v-else) ---

                    v-col(cols="12" md="4")
                        .mb-3
                            .text-caption.font-weight-bold.text-uppercase.mb-1(style="color: rgba(0, 0, 0, 0.6);") Reporter
                            .text-body-2
                                CopyableText(
                                    v-if="ticket.first_name || ticket.last_name"
                                    :text="(ticket.first_name.toString() + ' ' + ticket.last_name.toString()).trim()"
                                )
                                template(v-else) ---
</template>

<script lang="ts">
    import Vue from "vue";
    import VolieState from "@/store";

    // Components
    import CopyableText from "@/components/shared/copyable_text.vue";
    import TooltipText from "@/components/shared/tooltip_text.vue";
    import {
        TiptapVuetify,
        Heading,
        Bold,
        Italic,
        Strike,
        Underline,
        // Code,
        Paragraph,
        BulletList,
        OrderedList,
        ListItem,
        Link,
        Blockquote,
        HardBreak,
        // HorizontalRule,
        // History,
    } from "tiptap-vuetify";

    export default Vue.extend({
        data() {
            return {
                extensions: [
                    Blockquote,
                    Link,
                    Underline,
                    Strike,
                    Italic,
                    ListItem,
                    BulletList,
                    OrderedList,
                    [
                        Heading,
                        {
                            options: {
                                levels: [1, 2, 3],
                            },
                        },
                    ],
                    Bold,
                    Paragraph,
                    HardBreak,
                ],
            };
        },
        computed: {
            toolbarBackground() {
                return import.meta.env.VITE_APP_VOLIE_DETAILS_COMPONENT_TOOLBAR_BACKGROUND_IMG;
            },
            validTicket() {
                return this.ticket?.ticket_key?.length > 0;
            },
            jiraIssueLink() {
                if (!(this.ticket?.jira_issue_key?.length > 0)) {
                    return '';
                }

                return 'https://volie.atlassian.net/browse/' + this.ticket.jira_issue_key;
            },
            currentUser() {
                return VolieState.currentUser();
            },
            currentUserIsSuperAdmin() {
                return this.currentUser?.activated_role_id === 1;
            },
        },
        methods: {
            getTypeClass(type: string): string {
                switch (type) {
                    case 'bug': return 'error--text';
                    case 'enhancement': return 'success--text';
                    case 'question': return 'info--text';
                    default: return 'grey--text';
                }
            },
            getStatusClass(status: string): string {
                switch (status) {
                    case 'done': return 'success--text';
                    case 'in_progress': return 'info--text';
                    case 'in_review': return 'warning--text';
                    case 'open': return 'orange--text';
                    default: return 'grey--text';
                }
            },
        },
        props: {
            editable: Boolean,
            ticket: Object,
            showSnackbar: Function,
        },
        components: {
            CopyableText,
            TiptapVuetify,
            TooltipText,
        },
    });
</script>

<style lang="scss" scoped>
.v-card {
    border-radius: 12px !important;
    overflow: hidden;
    transition: all 0.3s ease;
    
    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
    }
}

.v-card__title {
    border-radius: 12px 12px 0 0 !important;
}

.ticket-key-chip {
    background: white !important;
    color: #1976d2 !important;
    font-size: 14px !important;
    font-weight: 700 !important;
    letter-spacing: 1px !important;
    height: 28px !important;
    border-radius: 6px !important;
    box-shadow: 0 1px 4px rgba(25, 118, 210, 0.08);
    padding: 0 12px !important;
    display: flex;
    align-items: center;
}

.account-label {
    color: white !important;
    font-size: 13px !important;
    font-weight: 700 !important;
    letter-spacing: 0.5px !important;
    margin-left: 2px !important;
    opacity: 0.92;
}

.v-chip {
    font-weight: 500;
    letter-spacing: 0.5px;
}

.text-caption {
    letter-spacing: 0.5px;
}

@media (max-width: 768px) {
    .v-col {
        margin-bottom: 16px;
    }
}
</style>
